import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';

export default function ProductList() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  // Fetch products on component mount
  useEffect(() => {
    fetchProducts();
  }, []);

  // Fetch all products from Firestore
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'products'));
      const productsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProducts(productsData);
    } catch (error) {
      console.error('Error fetching products:', error);
      setMessage('❌ Error fetching products: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Toggle product active status
  const toggleProductStatus = async (productId, currentStatus) => {
    try {
      await updateDoc(doc(db, 'products', productId), {
        active: !currentStatus,
        updatedAt: new Date()
      });
      
      // Update local state
      setProducts(prev => prev.map(product => 
        product.id === productId 
          ? { ...product, active: !currentStatus, updatedAt: new Date() }
          : product
      ));
      
      setMessage('✅ Product status updated successfully!');
    } catch (error) {
      console.error('Error updating product status:', error);
      setMessage('❌ Error updating product status: ' + error.message);
    }
  };

  // Delete product
  const deleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'products', productId));
        
        // Update local state
        setProducts(prev => prev.filter(product => product.id !== productId));
        
        setMessage('✅ Product deleted successfully!');
      } catch (error) {
        console.error('Error deleting product:', error);
        setMessage('❌ Error deleting product: ' + error.message);
      }
    }
  };

  // Format price with discount
  const formatPrice = (product) => {
    let finalPrice = product.basePrice;
    
    // Apply discount
    if (product.discount?.type === 'percentage' && product.discount?.value) {
      finalPrice = finalPrice * (1 - product.discount.value / 100);
    } else if (product.discount?.type === 'fixed' && product.discount?.value) {
      finalPrice = Math.max(0, finalPrice - product.discount.value);
    }
    
    return `$${finalPrice.toFixed(2)}`;
  };

  // Format options for display
  const formatOptions = (product) => {
    if (!product.options || product.options.length === 0) {
      return 'No options';
    }
    
    return product.options.map(option => 
      `${option.name}: ${option.choices.join(', ')}`
    ).join(' | ');
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date.toDate ? date.toDate() : date).toLocaleDateString();
  };

  if (loading) {
    return <div className="loading">Loading products...</div>;
  }

  return (
    <div className="product-list-container">
      <div className="list-header">
        <h2>Product Management</h2>
        <button onClick={fetchProducts} className="refresh-button">
          🔄 Refresh
        </button>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      {/* Products Table */}
      {products.length === 0 ? (
        <div className="no-products">
          <p>No products found. Add your first product to get started!</p>
        </div>
      ) : (
        <div className="products-table-container">
          <table className="products-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Title</th>
                <th>Category</th>
                <th>Base Price</th>
                <th>Final Price</th>
                <th>Options</th>
                <th>Discount</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map(product => (
                <tr key={product.id} className={!product.active ? 'inactive' : ''}>
                  {/* Product Image */}
                  <td className="product-image">
                    {product.images && product.images.length > 0 ? (
                      <img 
                        src={product.images[0]} 
                        alt={product.title}
                        className="thumbnail"
                      />
                    ) : (
                      <div className="no-image">No Image</div>
                    )}
                  </td>

                  {/* Product Title */}
                  <td className="product-title">
                    <strong>{product.title}</strong>
                    <p className="description">{product.description}</p>
                  </td>

                  {/* Category */}
                  <td>{product.category || 'Uncategorized'}</td>

                  {/* Base Price */}
                  <td>${product.basePrice?.toFixed(2) || '0.00'}</td>

                  {/* Final Price */}
                  <td className="final-price">
                    <strong>{formatPrice(product)}</strong>
                  </td>

                  {/* Options */}
                  <td className="product-options">
                    {formatOptions(product)}
                  </td>

                  {/* Discount */}
                  <td className="discount">
                    {product.discount?.type === 'none' ? (
                      'No discount'
                    ) : (
                      <span className={`discount-${product.discount?.type}`}>
                        {product.discount?.type === 'percentage' 
                          ? `${product.discount?.value}%` 
                          : `$${product.discount?.value}`
                        }
                      </span>
                    )}
                  </td>

                  {/* Status */}
                  <td>
                    <span className={`status ${product.active ? 'active' : 'inactive'}`}>
                      {product.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>

                  {/* Created Date */}
                  <td>{formatDate(product.createdAt)}</td>

                  {/* Actions */}
                  <td className="actions">
                    <button
                      onClick={() => toggleProductStatus(product.id, product.active)}
                      className={`status-button ${product.active ? 'deactivate' : 'activate'}`}
                    >
                      {product.active ? '🔄 Deactivate' : '✅ Activate'}
                    </button>
                    
                    <button
                      onClick={() => deleteProduct(product.id)}
                      className="delete-button"
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary */}
      <div className="products-summary">
        <p>Total Products: <strong>{products.length}</strong></p>
        <p>Active Products: <strong>{products.filter(p => p.active).length}</strong></p>
        <p>Inactive Products: <strong>{products.filter(p => !p.active).length}</strong></p>
      </div>
    </div>
  );
}
